<!-- eslint-disable vue/no-mutating-props -- detailPreferences is a shared reactive array, mutation is intentional -->
<template>
  <!-- Project Detail Modal -->
  <ProspectDetailModal
    :open="!!selectedProspect"
    :prospect="selectedProspect"
    :workflow-status="detailWorkflowStatus"
    :teams="detailTeams"
    :responsible-team-id="selectedResponsibleTeamId"
    :can-edit-team="detailCanEditTeam"
    :on-update-team="
      teamId => {
        $emit('update-team', teamId)
      }
    "
    :team-error="teamError"
    @close="$emit('close')"
    @prospect-updated="$emit('prospect-updated', $event)"
  >
    <!-- Workflow Status Banner - show for rejected and te_bespreken -->
    <WorkflowStatusBanner
      v-if="
        detailWorkflowStatus?.status === 'afgekeurd' ||
        detailWorkflowStatus?.status === 'te_bespreken' ||
        detailWorkflowStatus?.status === 'heraanvraag'
      "
      :status="detailWorkflowStatus"
      class="mb-4"
    />

    <!-- Read-only banner voor niet-bewerkbare kansen -->
    <div
      v-if="detailInitialLoadComplete && !detailCanEdit && detailWorkflowStatus"
      class="mb-4 p-3 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
    >
      <p class="text-sm text-gray-600 dark:text-gray-300">
        Deze kans wordt momenteel verwerkt en kan niet meer aangepast worden.
      </p>
    </div>

    <!-- Employee Preferences Section -->
    <div>
      <div class="mb-4">
        <h3 class="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
          <UsersIcon class="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
          Medewerker Voorkeuren
        </h3>
      </div>

      <!-- Loading state for preferences -->
      <div v-if="isLoadingDetail" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Laden...</p>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="detailPreferences.length === 0"
        class="text-center py-8 text-gray-500 dark:text-gray-400"
      >
        <UsersIcon class="h-12 w-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
        <p>Nog geen voorkeuren toegevoegd</p>
        <p class="text-sm">Klik op "Rol Toevoegen" om te beginnen</p>
      </div>

      <!-- Preferences List -->
      <div v-else class="space-y-4">
        <EmployeePreferenceCard
          v-for="(preference, index) in detailPreferences"
          :key="index"
          v-model="detailPreferences[index]"
          :index="index"
          :teams="detailTeams"
          :employees="detailEmployees"
          :experience-levels="uniqueFunctions"
          :can-edit="detailCanEdit"
          :is-invalid="detailInvalidDpwIndexes.has(index)"
          :prospect="selectedProspect"
          :weekly-availability="detailWeeklyAvailability"
          :calculate-match-percentage="
            (emp, pref) => calculateMatchPercentage(emp, pref, selectedProspect)
          "
          :is-perfect-match="(emp, pref) => isPerfectMatch(emp, pref, selectedProspect)"
          :get-filtered-employees="pref => getFilteredEmployees(pref, selectedProspect)"
          :get-selected-employee-names="
            (ids, pref) => getSelectedEmployeeNames(ids, pref, selectedProspect)
          "
          @remove="$emit('remove-preference', index)"
          @dpw-changed="$emit('dpw-changed', index)"
        />
      </div>
    </div>

    <template #footer>
      <!-- Message display -->
      <div
        v-if="detailSaveMessage"
        class="mb-3 p-3 rounded-lg"
        :class="
          isDetailErrorMessage
            ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
            : 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300'
        "
      >
        {{ detailSaveMessage }}
      </div>

      <div class="flex justify-between items-center gap-3">
        <!-- Left side: Rol Toevoegen, Indienen, Spoed -->
        <div class="flex gap-2">
          <button
            v-if="detailCanEdit"
            @click="$emit('add-preference')"
            :disabled="detailIsSaving || detailIsSubmitting || isMarkingUrgent"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 dark:focus:ring-offset-gray-900"
          >
            + Rol Toevoegen
          </button>

          <!-- Submit Button -->
          <button
            v-if="detailCanSubmit"
            @click="$emit('submit')"
            :disabled="detailIsSaving || detailIsSubmitting || isMarkingUrgent"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-400 dark:focus:ring-offset-gray-900"
          >
            {{ detailIsSubmitting ? 'Indienen...' : 'Indienen voor Beoordeling' }}
          </button>

          <!-- Resubmit Button -->
          <button
            v-if="detailCanResubmit"
            @click="$emit('resubmit')"
            :disabled="detailIsSaving || detailIsSubmitting || isMarkingUrgent"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-orange-400 dark:focus:ring-offset-gray-900"
          >
            {{ detailIsSubmitting ? 'Opnieuw indienen...' : 'Opnieuw Indienen' }}
          </button>

          <!-- Spoed Button -->
          <button
            v-if="canMarkUrgent"
            @click="$emit('spoed-click')"
            :disabled="detailIsSaving || detailIsSubmitting || isMarkingUrgent"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-400 dark:focus:ring-offset-gray-900"
          >
            Spoed
          </button>
        </div>

        <!-- Right side: Update and Close -->
        <div class="flex gap-3">
          <!-- Update Button - only visible after submission -->
          <button
            v-if="detailCanEdit && detailPreferences.length > 0 && isAlreadySubmitted"
            @click="$emit('save-with-tracking')"
            :disabled="detailIsSaving || detailIsSubmitting"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500"
          >
            {{ detailIsSaving ? 'Bijwerken...' : 'Voorkeuren Bijwerken' }}
          </button>

          <!-- Close Button -->
          <button
            @click="$emit('close')"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
          >
            Sluiten
          </button>
        </div>
      </div>
    </template>
  </ProspectDetailModal>

  <!-- Confirm Close Modal -->
  <ConfirmCloseModal
    :open="showConfirmClose"
    @discard="$emit('discard-and-close')"
    @cancel="$emit('cancel-close')"
  />

  <!-- Spoed Confirm Modal -->
  <ConfirmActionModal
    :open="showSpoedConfirm"
    :is-loading="isMarkingUrgent"
    title="Spoed markering"
    message="Weet je zeker dat je deze klus met spoed wilt indienen? De PL's worden dan direct geinformeerd."
    confirm-label="Ja, markeer als spoed"
    variant="danger"
    @confirm="$emit('mark-urgent')"
    @cancel="$emit('cancel-spoed')"
  />
</template>

<script setup>
import { UsersIcon } from '@heroicons/vue/24/outline'
import ProspectDetailModal from '@/components/common/modals/ProspectDetailModal.vue'
import WorkflowStatusBanner from '@/components/project-detail/WorkflowStatusBanner.vue'
import EmployeePreferenceCard from '@/components/project-detail/EmployeePreferenceCard.vue'
import ConfirmCloseModal from '@/components/common/modals/ConfirmCloseModal.vue'
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'

defineEmits([
  'close',
  'prospect-updated',
  'update-team',
  'add-preference',
  'remove-preference',
  'dpw-changed',
  'submit',
  'resubmit',
  'spoed-click',
  'save-with-tracking',
  'discard-and-close',
  'cancel-close',
  'mark-urgent',
  'cancel-spoed'
])

defineProps({
  selectedProspect: {
    type: Object,
    default: null
  },
  detailWorkflowStatus: {
    type: Object,
    default: null
  },
  detailTeams: {
    type: Array,
    default: () => []
  },
  selectedResponsibleTeamId: {
    type: String,
    default: null
  },
  detailCanEditTeam: {
    type: Boolean,
    default: false
  },
  teamError: {
    type: Boolean,
    default: false
  },
  detailInitialLoadComplete: {
    type: Boolean,
    default: false
  },
  detailCanEdit: {
    type: Boolean,
    default: false
  },
  isLoadingDetail: {
    type: Boolean,
    default: false
  },
  detailPreferences: {
    type: Array,
    default: () => []
  },
  detailEmployees: {
    type: Array,
    default: () => []
  },
  detailWeeklyAvailability: {
    type: Object,
    default: () => ({})
  },
  detailInvalidDpwIndexes: {
    type: Set,
    default: () => new Set()
  },
  uniqueFunctions: {
    type: Array,
    default: () => []
  },
  calculateMatchPercentage: {
    type: Function,
    required: true
  },
  isPerfectMatch: {
    type: Function,
    required: true
  },
  getFilteredEmployees: {
    type: Function,
    required: true
  },
  getSelectedEmployeeNames: {
    type: Function,
    required: true
  },
  detailSaveMessage: {
    type: String,
    default: ''
  },
  isDetailErrorMessage: {
    type: Boolean,
    default: false
  },
  detailCanSubmit: {
    type: Boolean,
    default: false
  },
  detailCanResubmit: {
    type: Boolean,
    default: false
  },
  detailIsSaving: {
    type: Boolean,
    default: false
  },
  detailIsSubmitting: {
    type: Boolean,
    default: false
  },
  isMarkingUrgent: {
    type: Boolean,
    default: false
  },
  canMarkUrgent: {
    type: Boolean,
    default: false
  },
  isAlreadySubmitted: {
    type: Boolean,
    default: false
  },
  showConfirmClose: {
    type: Boolean,
    default: false
  },
  showSpoedConfirm: {
    type: Boolean,
    default: false
  }
})
</script>
